Ext.define('SA.lq.view.ViewControllerFactory', {
    singleton: true,

    createAttributeSelectorCmp() {
        var cmp = Ext.widget({xtype: 'lq-attribute-selector'});

        cmp.listCmp = cmp.down('#listCmp');
        cmp.textField = cmp.down('#textField');
        cmp.textField.on('change', cmp.refreshItems, cmp);

        return cmp;
    },

    createController({model}) {
        var toolbar = SA.lq.view.ToolbarFactory.create(),
            toolbarExportBtn = toolbar.cmps.exportBtn,
            conditionCmpFactory = new SA.lq.view.ConditionCmpFactory(),
            attributeSelectorCmp = this.createAttributeSelectorCmp(),
            view = this.createView({attributeSelectorCmp}),
            projectView = SA.ProjectView.createHidden(view),
            searchBtn = view.down('#searchBtn'),
            resultsPanel = view.down('#resultsPanel'),
            resultsHeader = view.down('#resultsHeader'),
            resultsGrid = view.down('#resultsGrid'),
            searchQueryFactory = this.createSearchQueryFactory({model}),
            conditionListCmp = new SA.lq.view.ConditionList({
                listCmp: view.down('#conditionList'),
                cmpFactory: conditionCmpFactory,
                modelFactory: new SA.model.ConditionFactory()
            }),
            gridColumnMenuFactory = new SA.lq.view.GridColumnMenuFactory(),
            gridSizer = new SA.lq.view.GridSizer({
                grid: resultsGrid,
                getPanelSize: () => resultsPanel.getSize(),
                getHeaderHeight: () => resultsHeader.getHeight(),

                layoutConsts: {
                    paddingBottom: 24,
                    paddingTop: 24,
                    paddingLeft: 32,
                    bodyMarginTop: 24
                }
            }),
            locationCellMenuItemsFactory = new SA.lq.view.LocationCellMenuItemsFactory({
                getCaptionHtml: ({name, alias}) => Ext.util.Format.locationRenderer({name, locationAlias: alias}),
                canAddToProject: locationSeries => !model.project.containsLocation(locationSeries),
                canAddToFavorites: locationSeries => !SA.data.FavoriteLocations.contains(locationSeries)
            }),
            searchController = new SA.lq.view.SearchController({
                gridSizer,
                queryFactory: searchQueryFactory,
                resultsHeader,
                grid: resultsGrid
            }),
            controller = new SA.lq.view.ViewController({
                model,
                searchController,
                gridColumnMenuFactory,
                locationCellMenuItemsFactory,
                gridSizer,
                exportActivityFactory: this.createExportActivityFactory({model, toolbarExportBtn}),

                cmps: {
                    toolbar,
                    projectView,
                    view,
                    searchBtn,
                    conditionList: conditionListCmp,
                    attributeSelector: attributeSelectorCmp,
                    resultsPanel,
                    resultsGrid,
                    geoUnitsCombo: new SA.lq.view.GeoUnitsCombo(toolbar.cmps.geoUnitsCombo),
                    locationsCombo: new SA.lq.view.LocationsCombo(toolbar.cmps.locationsCombo),
                    toolbarExportBtn
                }
            }),
            gridColumnEventHandler = new SA.lq.view.GridColumnEventHandler({
                matchCol: ({dataIndex}) => dataIndex !== 'rank',
                clickCb: params => controller.onGridColumnClick(params)
            }),
            gridLocationCellEventHandler = new SA.lq.view.GridCellEventHandler({
                matchCell: ({dataIndex}) => dataIndex === 'name',
                clickCb: params => controller.onGridLocationCellClick(params)
            });

        resultsGrid.on('column:click', gridColumnEventHandler.onClick, gridColumnEventHandler);
        resultsGrid.on('column:mouseover', gridColumnEventHandler.onMouseOver, gridColumnEventHandler);
        resultsGrid.on('column:mouseout', gridColumnEventHandler.onMouseOut, gridColumnEventHandler);

        resultsGrid.on('cell:click', gridLocationCellEventHandler.onClick, gridLocationCellEventHandler);
        resultsGrid.on('cell:mouseover', gridLocationCellEventHandler.onMouseOver, gridLocationCellEventHandler);
        resultsGrid.on('cell:mouseout', gridLocationCellEventHandler.onMouseOut, gridLocationCellEventHandler);

        model.on('remove', () => searchController.cancelQuery());

        gridColumnMenuFactory.setSortItemClickHandler(sort => controller.onGridColumnMenuSortItemClick(sort));

        conditionCmpFactory.setHandlers({
            fieldChange: () => controller.onConditionListCmpChange(),
            conditionOperatorComboChange: () => controller.onConditionListCmpChange(),
            listOperatorComboChange: value => conditionListCmp.setListOperatorValue(value),
            removeClick: cmp => conditionListCmp.removeItem(cmp)
        });

        searchQueryFactory.setSuccessHandler(result => searchController.onQuerySuccess(result));

        projectView.on('resize', () => gridSizer.resize());
        attributeSelectorCmp.setItemClickHandler(attributeId => controller.onAddConditionIntent(attributeId));
        toolbar.cmps.locationsCombo.on('change', (_, locationStringId) => controller.onSelectLocationIntent(locationStringId));
        toolbar.cmps.geoUnitsCombo.on('change', (_, geoUnitId) => controller.onGeoUnitsComboChange(geoUnitId));
        conditionListCmp.setChangeHandler(() => controller.onConditionListCmpChange());
        searchBtn.on('click', () => controller.onSearchBtnClick());

        toolbarExportBtn.on('click', () => controller.onToolbarExportBtnClick(), null, {single: true});

        toolbar.cmps.editViewMenuItem.on('click', () => SA.AppController.showProjectView(model, 'edit'));
        toolbar.cmps.deleteViewMenuItem.on('click', () => SA.AppController.deleteView(model));

        return controller;
    },

    createExportActivityFactory({model, toolbarExportBtn}) {
        return new SA.lq.view.ExportActivityFactory({
            toolbarExportBtn,

            widgetFactory: new SA.lq.view.ExportWidgetFactory({
                userEmailAddress: SA.User.getEmailAddress(),
                canEnableExportBtn: widget => widget.deliveryTypeSwitchValueIsDownload() || widget.hasValidEmailAddressValue()
            }),

            requestFactory: new SA.lq.ExportRequestFactory({
                model,
                countryCode: model.getCountry(),
                censusReleaseYear: model.getCensusReleaseYear(),
                resultLimit: SA.data.Query.MAX_LIMIT,
                baseColumns: ['name', 'spatialId']
            })
        });
    },

    createSearchQueryFactory({model}) {
        return new SA.lq.SearchQueryFactory({
            resultLimit: SA.data.Query.MAX_LIMIT,
            censusReleaseYear: model.getCensusReleaseYear(),

            gridColumnCfgFactory: new SA.lq.GridColumnCfgFactory({
                getAttributeColumnHeaderHtml: attributeId => SA.data.AttributesCache.getBadgedLabelHtml(attributeId)
            })
        });
    },

    createView({attributeSelectorCmp}) {
        return Ext.widget({
            xtype: 'lq-view',

            attributesPanel: {
                xtype: 'simple-container',
                cls: 'sa-lq-attributes-panel',

                items: [
                    {
                        xtype: 'component',
                        cls: 'sa-lq-attributes-panel-heading',
                        html: 'Variables in this project'
                    },
                    attributeSelectorCmp
                ]
            }
        });
    }
});